/**
 * Project: Academy V2
 * File: ClassReportLayout.tsx
 * Author: Colopi Salvatore
 * Author URI: https://salvatorecolopi.com
 * Created: Wed Jul 10 2024 at 19:08:39
 *
 * Copyright (c) 2024 Edusogno. All rights reserved.
 */

import DashboardContentLayout from "@/layouts/DashboardContentLayout.tsx";
import useGlobals from "@/utils/hooks/useGlobals.ts";
import { PropsWithChildren, useEffect } from "react";
import { ExerciseType } from "@/components/v3/exercises/ExerciseBadge";
import LayoutHeader from "@/components/v3/exercises/LayoutHeader";
import { cn } from "@edusogno/lib/utils";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import InnerClassReport from "./InnerClassReport";
import { NavigationState } from "@/types";
import { useUserReports } from "@/data/class-reports";
import { SpinnerLoader } from "../ui/Loader";

type Props = {
  title?: string;
  type?: ExerciseType;
  onClose?: () => void;
};

export default function ClassReportLayout({
  // children,
  title = "Multiple choice and matching options (pt.2)",
  type = ExerciseType.READING,
  onClose,
}: PropsWithChildren<Props>) {
  const navigate = useNavigate();
  const { userId, eventId } = useParams();
  const { setGlobalState } = useGlobals();
  const location = useLocation();
  const state = location.state as NavigationState;
  const { typeCR } = state || {};
  const { reports, isLoading } = useUserReports({
    userId: userId ?? '',
    eventId: eventId ?? ''
  });

  useEffect(() => {
    setGlobalState("backgroundClass", "bg-brand-background");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setGlobalState("showSideBar", false);
    return () => {
      setGlobalState("showSideBar", true);
    }

  }, []);

  const handleClose = onClose || (() => {
    navigate(-1);
  });

  return (
    <DashboardContentLayout>
      <div className="flex flex-col h-full gap-4">
        <LayoutHeader
          title={title}
          onClose={handleClose}
          type={typeCR || type}
          date={reports?.data?.date}
          time={reports?.data?.time}
        />
        <div
          className={cn("flex rounded-[15px] flex-col w-full relative h-full until-md:h-4/5 bg-white/40 p-4")}
        >
          {isLoading ? (
            <div className="mx-auto">
              <SpinnerLoader />
            </div>
          ) : (
            <InnerClassReport
              grammar={reports?.data?.grammar || []}
              vocabulary={reports?.data?.vocabulary || []}
              pronunciation={reports?.data?.pronunciation || []}
            />
          )}
        </div>
      </div>
    </DashboardContentLayout>
  );
}
